import { useState, useRef, useCallback, useEffect } from 'react';
import { AudioRecorder } from '../lib/audio-recorder';

interface UseAudioRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  isTesting: boolean;
  duration: number;
  volumeLevel: number;
  error: string | null;
  isSupported: boolean;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  startTesting: () => Promise<void>;
  stopTesting: () => void;
  startRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => Promise<{ blob: Blob; duration: number } | null>;
  cancelRecording: () => void;
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<AudioRecorder | null>(null);
  const intervalRef = useRef<number | null>(null);
  const volumeIntervalRef = useRef<number | null>(null);

  const isSupported = AudioRecorder.isSupported();

  const startVolumeMonitoring = useCallback(() => {
    // Update volume level frequently for smooth visualization
    volumeIntervalRef.current = window.setInterval(() => {
      if (recorderRef.current) {
        setVolumeLevel(recorderRef.current.getVolumeLevel());
      }
    }, 50); // 20 times per second
  }, []);

  const stopVolumeMonitoring = useCallback(() => {
    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
      setVolumeLevel(0);
    }
  }, []);

  const startTesting = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser');
      return;
    }

    try {
      setError(null);
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.initializeMicrophone();
      setIsTesting(true);
      startVolumeMonitoring();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize microphone';
      setError(errorMessage);
      setIsTesting(false);
    }
  }, [isSupported, startVolumeMonitoring]);

  const stopTesting = useCallback(() => {
    stopVolumeMonitoring();
    if (recorderRef.current) {
      recorderRef.current.cancel();
      recorderRef.current = null;
    }
    setIsTesting(false);
  }, [stopVolumeMonitoring]);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser');
      return;
    }

    try {
      setError(null);

      // If testing, use existing recorder; otherwise create new one
      if (!recorderRef.current) {
        recorderRef.current = new AudioRecorder();
      }

      await recorderRef.current.start();
      setIsRecording(true);
      setIsTesting(false);
      setDuration(0);

      // Update duration every second
      intervalRef.current = window.setInterval(() => {
        if (recorderRef.current) {
          setDuration(recorderRef.current.getRecordingTime());
        }
      }, 1000);

      // Start volume monitoring if not already started
      if (!volumeIntervalRef.current) {
        startVolumeMonitoring();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
      setError(errorMessage);
      setIsRecording(false);
    }
  }, [isSupported, startVolumeMonitoring]);

  const pauseRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.pause();
      setIsPaused(true);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.resume();
      setIsPaused(false);
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    stopVolumeMonitoring();

    if (recorderRef.current) {
      recorderRef.current.cancel();
      recorderRef.current = null;
    }

    setIsRecording(false);
    setIsPaused(false);
    setDuration(0);
  }, [stopVolumeMonitoring]);

  const stopRecording = useCallback(async (): Promise<{ blob: Blob; duration: number } | null> => {
    if (!recorderRef.current) {
      return null;
    }

    try {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      stopVolumeMonitoring();

      const result = await recorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      setDuration(0);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop recording';
      setError(errorMessage);
      setIsRecording(false);
      setIsPaused(false);
      return null;
    }
  }, [stopVolumeMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
      }
    };
  }, []);

  const audioContext = recorderRef.current?.getAudioContext() ?? null;
  const analyser = recorderRef.current?.getAnalyser() ?? null;

  return {
    isRecording,
    isPaused,
    isTesting,
    duration,
    volumeLevel,
    error,
    isSupported,
    audioContext,
    analyser,
    startTesting,
    stopTesting,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  };
}
